import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  connectRequestsToRequest,
  disconnectRequests,
  getAttachedRelatedServiceRequests,
} from '../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { useNotification } from '../../../../hooks'
import { getItemsIds } from '../../../../utils/common'
import { ConfirmationModal } from '../../../Confirmation'
import { TableModalConnectionActions } from '../../../TableModal'
import {
  AttachServiceRequestsModal,
  getRelatedRequestsLimitErrorMeta,
} from '../../AttachServiceRequestsModal'

export function useRequestTableAttachedRequests({
  requests,
  onCloseModal,
  onReload,
  onSetModal,
}) {
  const [tagActionModal, setTagActionModal] = useState(null)
  const tagModalRef = useRef(null)
  const connectModalRef = useRef(null)

  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  function handleCloseTagModal() {
    setTagActionModal(null)
  }

  async function handleAttachRequests({ id, selected, status }) {
    connectModalRef.current?.setLoading?.(true)

    return connectRequestsToRequest({
      data: {
        requests: getItemsIds(selected),
        ...(status
          ? {
              restore_archived_with_status: status,
            }
          : EMPTY_OBJECT),
      },
      id,
    })
      .then(() => {
        tagModalRef.current?.refresh?.()

        handleAddSuccessNotification({
          title: t('Notification.RequestTitleRequestsConnectedKey', {
            count: selected.length,
            totalCount: selected.length,
          }),
        })

        onReload()
        onCloseModal()
      })
      .catch(error => {
        if (getRelatedRequestsLimitErrorMeta(error)) {
          throw error
        }

        handleAddErrorNotification('Error.CommonApiRequestFailKey')
        onCloseModal()
      })
      .finally(() => {
        connectModalRef.current?.setLoading?.(false)
      })
  }

  function handleClickConnect(itemId) {
    const request = requests?.items.find(item => item.id === itemId)

    async function attach(values) {
      return handleAttachRequests({
        ...values,
        id: itemId,
      })
    }

    onSetModal(
      <AttachServiceRequestsModal
        multipleSelection
        showLimitNotification
        excludeIds={[itemId, ...(request.related_request_ids || EMPTY_LIST)]}
        ref={connectModalRef}
        onAttach={attach}
        onClose={onCloseModal}
      />
    )
  }

  async function disconnect({ disconnectAll, itemId, requests }) {
    tagModalRef.current?.setLoading?.(true)

    return disconnectRequests({ data: { requests }, id: itemId })
      .then(() => {
        onReload()

        handleAddSuccessNotification({
          title: t('Notification.RequestTitleRequestsDisconnectedKey', {
            count: requests.length,
            totalCount: requests.length,
          }),
        })

        if (disconnectAll) {
          handleCloseTagModal()
        } else {
          tagModalRef.current?.unselectAll?.()
          tagModalRef.current?.refresh?.()
        }
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        tagModalRef.current?.setLoading?.(false)
        onCloseModal()
      })
  }

  function handleClickDisconnect(id) {
    function submit() {
      disconnect({
        itemId: id,
        requests: getItemsIds(tagModalRef.current?.selected),
      })
    }

    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DisconnectRequestBtnKey'),
        }}
        description={t('Modal.DisconnectRequestAttentionMessageKey')}
        onCancel={onCloseModal}
        onSubmit={submit}
      />
    )
  }

  function handleClickDisconnectAll(id) {
    function submit() {
      disconnect({ disconnectAll: true, itemId: id, requests: [id] })
    }

    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t('Modal.DisconnectRequestBtnKey'),
        }}
        description={t('Modal.DisconnectAllRequestAttentionMessageKey')}
        onCancel={onCloseModal}
        onSubmit={submit}
      />
    )
  }

  function handleClickLinkedRequestTag({ itemId }) {
    const buttonConnectProps = {
      onClick: () => handleClickConnect(itemId),
    }

    const buttonDisconnectAllProps = {
      onClick: () => handleClickDisconnectAll(itemId),
    }

    const buttonDisconnectProps = {
      onClick: () => handleClickDisconnect(itemId),
    }

    setTagActionModal(
      <AttachServiceRequestsModal
        multipleSelection
        Actions={TableModalConnectionActions}
        actionsProps={{
          ButtonCancel: null,
          buttonAttachProps: {
            text: 'Modal.ConfirmBtnKey',
          },
          buttonConnectProps,
          buttonDisconnectAllProps,
          buttonDisconnectProps,
        }}
        counterProps={{
          text: 'Modal.AttachedRequestsCountBtnKey',
        }}
        entityId={itemId}
        headerProps={{
          text: 'Modal.AttachedRequestsTitleKey',
        }}
        loadItems={getAttachedRelatedServiceRequests}
        ref={tagModalRef}
        onClose={handleCloseTagModal}
      />
    )
  }

  return {
    handleClickLinkedRequestTag,
    tagModal: tagActionModal,
  }
}
